import * as d3 from 'importers/d3-importer'

/**
 * Creates an HTML amortization <table> using D3.
 * @class AmortizationTable
 */
export class ScrollyAmortizationTable {
  /**
   * Constructor - Called when creating a new instance of AmortizationTable
   *
   * @constructs
   * @param {Element} element container
   */
  constructor(element) {
    this.table           = d3.select(element)
    this.timeUnit        = this.table.select('#time-unit')
    this.timeColumn      = this.table.select('#time')
    this.dateColumn      = this.table.select('#date')
    this.paymentColumn   = this.table.select('#payment')
    this.interestColumn  = this.table.select('#interest')
    this.principalColumn = this.table.select('#principal')
    this.balanceColumn   = this.table.select('#balance')
  }

  /**
   * Defers building the table until the element scrolls into the viewport.
   * Falls back to immediate build when IntersectionObserver is unavailable.
   *
   * @param {Element} element - the DOM element to observe
   * @param {object} data - same shape as build()
   */
  buildDeferred(element, data) {
    if (!('IntersectionObserver' in window)) {
      this.build(data)
      return
    }

    this._deferredData = data
    this._observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        this._observer.disconnect()
        this._observer = null
        const pending = this._deferredData
        this._deferredData = null
        this.build(pending)
      }
    }, { rootMargin: '200px' })
    this._observer.observe(element)
  }

  /**
   * Builds the table with the six datasets
   *
   * @param {object} contains six arrays with data for time, date, payment, interest, principal, and balance
   */
  build({ timeUnit, timeData, dateData, paymentData, interestData, principalData, balanceData, listItemClass }) {
    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
      this._deferredData = null
    }
    this.timeUnit.text(timeUnit)

    const timeSelection      = this.timeColumn.selectAll('li').data(timeData)
    const dateSelection      = this.dateColumn.selectAll('li').data(dateData)
    const paymentSelection   = this.paymentColumn.selectAll('li').data(paymentData)
    const interestSelection  = this.interestColumn.selectAll('li').data(interestData)
    const principalSelection = this.principalColumn.selectAll('li').data(principalData)
    const balanceSelection   = this.balanceColumn.selectAll('li').data(balanceData)


    timeSelection.append('li').classed(`amortization-table__item ${listItemClass}`, true).text(d => d.time)
    dateSelection.append('li').classed(`amortization-table__item ${listItemClass}`, true).text(d => d.date)
    paymentSelection.append('li').classed(`amortization-table__item ${listItemClass}`, true).text(d => d.payment)
    interestSelection.append('li').classed(`amortization-table__item ${listItemClass}`, true).text(d => d.interest)
    principalSelection.append('li').classed(`amortization-table__item ${listItemClass}`, true).text(d => d.principal)
    balanceSelection.append('li').classed(`amortization-table__item ${listItemClass}`, true).text(d => d.balance)


    timeSelection
      .enter()
      .append('li')
      .classed(`amortization-table__item ${listItemClass}`, true)
      .merge(timeSelection)
      .text(d => d)
    dateSelection
      .enter()
      .append('li')
      .classed(`amortization-table__item ${listItemClass}`, true)
      .merge(dateSelection)
      .text(d => d)
    paymentSelection
      .enter()
      .append('li')
      .classed(`amortization-table__item ${listItemClass}`, true)
      .merge(paymentSelection)
      .text(d => d)
    interestSelection
      .enter()
      .append('li')
      .classed(`amortization-table__item ${listItemClass}`, true)
      .merge(interestSelection)
      .text(d => d)
    principalSelection
      .enter()
      .append('li')
      .classed(`amortization-table__item ${listItemClass}`, true)
      .merge(principalSelection)
      .text(d => d)
    balanceSelection
      .enter()
      .append('li')
      .classed(`amortization-table__item ${listItemClass}`, true)
      .merge(balanceSelection)
      .text(d => d)


    timeSelection.exit().remove()
    dateSelection.exit().remove()
    paymentSelection.exit().remove()
    interestSelection.exit().remove()
    principalSelection.exit().remove()
    balanceSelection.exit().remove()
  }
}

